import { useMemo } from "react";
import { motion } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Card } from "@/components/ui/card";
import { PieChart as PieIcon } from "lucide-react";
import { formatMoney } from "@/lib/money";

// Palette derived from the earthy theme
const PALETTE = ["#2D4A32", "#839788", "#C4D0C0", "#8C3A3A", "#B08968", "#5A6E5E", "#D9C6A5", "#3F5A47"];

function TooltipContent({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-background border border-border/60 rounded-md shadow-sm px-3 py-2 text-xs">
      <div className="font-medium">{p.category}</div>
      <div className="text-muted-foreground tabular-nums">
        {formatMoney(p.total)}
        <span className="ml-2 text-primary">{p.pct}%</span>
      </div>
    </div>
  );
}

export default function CategoryChart({ expenses, quarterLabel }) {
  const data = useMemo(() => {
    const map = new Map();
    let total = 0;
    for (const e of expenses || []) {
      const c = (e.category || "Other").trim() || "Other";
      const amt = Number(e.amount) || 0;
      map.set(c, (map.get(c) || 0) + amt);
      total += amt;
    }
    const items = Array.from(map.entries())
      .map(([category, sum]) => ({ category, total: sum, pct: total > 0 ? Math.round((sum / total) * 100) : 0 }))
      .sort((a, b) => b.total - a.total);
    return { items, total };
  }, [expenses]);

  if (!data.items.length) {
    return (
      <Card className="p-8 border-border/60 flex items-center justify-center text-muted-foreground text-sm" data-testid="category-chart-empty">
        Add expenses to see a category breakdown.
      </Card>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
      <Card className="p-6 border-border/60" data-testid="category-chart-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-muted-foreground">Spend by category</div>
            <div className="mt-1 font-display text-lg font-semibold tracking-tight">{quarterLabel}</div>
          </div>
          <PieIcon className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <div className="h-[220px]" data-testid="category-chart-svg">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.items}
                  dataKey="total"
                  nameKey="category"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                  stroke="none"
                >
                  {data.items.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip content={<TooltipContent />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2">
            {data.items.map((item, i) => (
              <div key={item.category} className="flex items-center justify-between text-sm" data-testid={`category-legend-${item.category}`}>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} />
                  <span className="font-medium">{item.category}</span>
                  <span className="text-muted-foreground text-xs">· {item.pct}%</span>
                </div>
                <span className="tabular-nums font-medium">
                  {formatMoney(item.total)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
